import React, { FC, ReactNode, useEffect, useState } from 'react';
import { SectionFormContext } from './context';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';

import useDashboardContext from 'contexts/dashboard.context';
import { ReformedBrandItemType, SectionFormType } from './types';

import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { BrandType } from 'utils/types';

const SectionFormProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [isLoadingBrandItems, setIsLoadingBrandItems] = useState(false);

  const [sectionForm, setSectionForm] = useState<SectionFormType>({
    sectionName: '',
    selectedBrand: [],
    selectedItems: [],
    selectedPolygon: [],
    status: true,
    brandSearch: '',
    brandItemSearch: '',
    images: []
  });
  const [disabled, setDisabled] = useState(false);
  const { activeCokitchen } = useDashboardContext();
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [brandItems, setBrandItems] = useState<ReformedBrandItemType[]>([]);

  const handleChange = (name: string, value: string | string[] | boolean) => {
    setSectionForm((prevFormData) => {
      return {
        ...prevFormData,
        [name]: value
      };
    });
  };

  const handleToggle = async (polygonId?: string) => {
    if (!polygonId) return;

    const polygonToUpdate = sectionForm.selectedPolygon.find((p) => p.id === polygonId);
    if (!polygonToUpdate) return;

    const newAvailable = !polygonToUpdate.available;

    try {
      const updatedLocal = sectionForm.selectedPolygon.map((p) =>
        p.id === polygonId ? { ...p, available: newAvailable } : p
      );

      setSectionForm({
        ...sectionForm,
        selectedPolygon: updatedLocal
      });

      toast(<ToastMessage type='success' message={`Updated polygon successfully`} />);
    } catch (error) {
      toast(<ToastMessage type='error' message={`Failed to update polygon`} />);
    }
  };

  const fetchBrands = async () => {
    setIsLoadingBrands(true);
    const params = { cokitchen_id: activeCokitchen.cokitchen_id };
    try {
      const res = await apiService.get('brand_management/brands', { params });

      setBrands(res.data);
      setIsLoadingBrandItems(false);
    } catch (error) {
      setBrands([]);
      handleApiError(error);
    } finally {
      setIsLoadingBrandItems(false);
    }
  };

  const fetchBrandItems = async () => {
    setIsLoadingBrandItems(true);
    try {
      const data = { brand_ids: sectionForm.selectedBrand };
      const res = await apiService.post(`meals`, { data, isInternal: true });
      const meals = res.data.data;
      const items = meals.map((parentMeal: any) => {
        return {
          value: parentMeal?.id,
          text: `${parentMeal?.brand?.name} - ${parentMeal?.name}`,
          category_id: parentMeal?.meal_category?.id
        };
      });
      setBrandItems(items);
      setIsLoadingBrandItems(false);
    } catch {
      setBrandItems([]);
    } finally {
      setIsLoadingBrandItems(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    setSectionForm({
      ...sectionForm,
      selectedPolygon: activeCokitchen.cokitchen_polygons.map((p) => ({
        ...p,
        available: true
      }))
    });
  }, [activeCokitchen]);
  useEffect(() => {
    fetchBrandItems();
  }, [sectionForm.selectedBrand]);

  return (
    <SectionFormContext.Provider
      value={{
        sectionForm,
        setSectionForm,
        disabled,
        setDisabled,
        handleChange,
        brands,
        brandItems,
        isLoadingBrandItems,
        isLoadingBrands,
        handleToggle
      }}>
      {children}
    </SectionFormContext.Provider>
  );
};

export default SectionFormProvider;
